import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import { NodeRegistration } from '../config/nodeRegistry';
import { getModelCreditRange } from '../utils/creditCostUtils';
import { CreditsIcon } from './ui/CreditsIcon';
import './ModelTooltip.css';

// Media type colors matching design system
const MEDIA_TYPE_COLORS: Record<string, string> = {
  text: '#FED402',    // Yellow
  image: '#42C3D7',   // Cyan
  video: '#FF4B42',   // Red
};

interface ModelTooltipProps {
  node: NodeRegistration;
  children: React.ReactElement;
  disabled?: boolean;
}

/**
 * Palette credit range for nodes that declare NO `selectedModel` anywhere —
 * neither in `initialData` nor as a setting default — because their model is
 * derived from a different setting at generation time.
 *
 * KlingO3Video derives it from `quality` (standard / pro), so 'standard' is the
 * id the palette would produce. Every entry must name the setting it mirrors,
 * and must be for a node that genuinely has no `selectedModel` — the test
 * `src/config/__tests__/uiReachableModels.test.ts` enforces both.
 */
export const DERIVED_MODEL_NODE_FALLBACKS: Record<string, string> = {
  // KLING_O3_VIDEO_NODE_DEF has a `quality` setting defaulting to 'standard'.
  'KlingO3Video': 'kling-3.0-standard',
};

/**
 * Custom tooltip component for model cards in the NodePalette
 * Shows credits, input/output types, model name, and description
 */
export const ModelTooltip: React.FC<ModelTooltipProps> = ({
  node,
  children,
  disabled = false
}) => {
  if (disabled) return children;
  const definition = node.definition;
  
  // Get accepted input types from definition
  const acceptsMediaTypes = definition.acceptsMediaTypes || [];
  
  // Get output type from definition
  const outputsMediaType = definition.outputsMediaType;
  
  // Get credit range - derived from the node's own declarations, never mirrored.
  const getModelId = (): string | null => {
    // 1. The id the palette actually stamps onto a new node.
    if (node.initialData?.selectedModel) {
      return node.initialData.selectedModel as string;
    }
    // 2. The node definition's own selectedModel default. Reached when
    //    initialData pins nothing, or pins the empty-string sentinel used by
    //    nodes whose model is derived from another setting.
    const modelSetting = definition.settings?.find(s => s.id === 'selectedModel');
    if (typeof modelSetting?.default === 'string' && modelSetting.default) {
      return modelSetting.default;
    }
    // 3. Nodes with no selectedModel anywhere, whose model is derived from a
    //    different setting entirely. There is nothing to read, so the id has to
    //    be stated — but ONLY for these, and NODE_MODEL_FALLBACKS_ARE_MINIMAL in
    //    src/config/__tests__/uiReachableModels.test.ts fails if an entry is
    //    added for a node that does declare a model.
    //
    //    This replaced a 26-entry mirror of every node's default. Because
    //    step 1 shadowed almost all of it, the stale halves ('ImagePrompt' ->
    //    'gemini-nano-banana', 'GPTImage' -> 'gpt-image-1', 'VeoVideo' ->
    //    'veo-3-falai', 'LTXVideo' -> 'ltx-2-pro', 'PixVerseVideo' ->
    //    'pixverse-5.5', 'MinimaxVideo' -> 'minimax-hailuo', 'RecraftV3' ->
    //    'recraft-v3', 'AgentPrompt' -> 'gpt-5-nano') were unreachable dead
    //    code rather than wrong numbers on screen — which is why nothing looked
    //    broken while they rotted.
    return DERIVED_MODEL_NODE_FALLBACKS[node.type] ?? null;
  };

  const modelId = getModelId();
  const creditRange = modelId ? getModelCreditRange(modelId) : null;
  
  // Render input/output indicator dots
  const renderIOIndicator = () => {
    return (
      <div className="model-tooltip-io">
        {/* Input types */}
        {acceptsMediaTypes.map((type, index) => (
          <span 
            key={`input-${index}`}
            className="io-dot"
            style={{ backgroundColor: MEDIA_TYPE_COLORS[type] || '#4A5568' }}
            title={`Accepts ${type}`}
          />
        ))}
        
        {/* Chevron separator */}
        {acceptsMediaTypes.length > 0 && outputsMediaType && (
          <span className="io-chevron">›</span>
        )}
        
        {/* Output type */}
        {outputsMediaType && (
          <span 
            className="io-dot"
            style={{ backgroundColor: MEDIA_TYPE_COLORS[outputsMediaType] || '#4A5568' }}
            title={`Outputs ${outputsMediaType}`}
          />
        )}
      </div>
    );
  };
  
  // Tooltip content
  const tooltipContent = (
    <div className="model-tooltip-content">
      {/* Header row: Credits and I/O indicator */}
      <div className="model-tooltip-header">
        <div className="model-tooltip-credits">
          {creditRange && (
            <>
              <CreditsIcon size={14} color="#a2a2a2" />
              <span>{creditRange.displayText}</span>
            </>
          )}
        </div>
        {renderIOIndicator()}
      </div>
      
      {/* Model name */}
      <div className="model-tooltip-name">
        {definition.title}
      </div>
      
      {/* Description */}
      <div className="model-tooltip-description">
        {node.description}
      </div>
    </div>
  );
  
  return (
    <Tooltip
      title={tooltipContent}
      placement="right-start"
      arrow
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: '#474747',
            maxWidth: 400,
            padding: 0,
            borderRadius: '12px',
            '& .MuiTooltip-arrow': {
              color: '#474747',
            },
          },
        },
        popper: {
          sx: { pointerEvents: 'none' },
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -2],
              },
            },
          ],
        },
      }}
    >
      {children}
    </Tooltip>
  );
};

export default ModelTooltip;
